import React, { useCallback, useEffect, useState } from 'react'

import {
  useGetArtboardAtPosition,
  useLockCamera,
  useMapRelativePositionToCameraPosition,
  useStatus,
} from '@sketch-hq/sketch-web-renderer'

import { DraftCommentProvider } from '@sketch/modules-common'
import { AnnotationOverlayContext } from '../../../annotations/components'
import { MouseCoordinates } from '../../../annotations/types'
import { AnnotationDotFragment } from '@sketch/gql-types'
import { useHideAnnotationDots } from '../../../annotations/hooks'

interface PreviewAnnotationOverlayContextProps
  extends Pick<
    React.ComponentProps<typeof AnnotationOverlayContext>,
    'disabled' | 'children' | 'hidden'
  > {
  isViewingLatestVersion: boolean
  permanentPageId: string
  /**
   * When set, the context scopes annotations to this single frame (frame view).
   * When absent, all annotations on the current page are shown (page view).
   */
  frameUUID?: string
}

export const PreviewAnnotationOverlayContext = (
  props: PreviewAnnotationOverlayContextProps
) => {
  const { permanentPageId, frameUUID, hidden, ...otherProps } = props

  const status = useStatus()
  const lockCamera = useLockCamera()
  const [lockCameraState, setLockCameraState] = useState(false)

  useEffect(() => {
    lockCamera(lockCameraState)
  }, [lockCamera, lockCameraState])

  const getArtboardAtPosition = useGetArtboardAtPosition()
  const mapRelativePosition = useMapRelativePositionToCameraPosition()

  const getElementAtPosition = useCallback(
    async (mouseCoordinates: MouseCoordinates) => {
      const artboard = await getArtboardAtPosition(
        mouseCoordinates.clientX,
        mouseCoordinates.clientY
      )

      if (artboard) {
        return {
          type: 'ARTBOARD' as const,
          permanentId: artboard.getLayerUUID(),
          bounds: artboard.getAbsoluteExtent(),
          permanentPageId,
        }
      }

      // In frame view there's no page-level fallback — annotations can only
      // be placed on the frame itself.
      if (frameUUID) {
        return null
      }

      return {
        type: 'PAGE' as const,
        permanentId: permanentPageId,
      }
    },
    [getArtboardAtPosition, permanentPageId, frameUUID]
  )

  const getPositionRelativeToOrigin = useCallback(
    (mouseCoordinates: MouseCoordinates) => {
      if (status.type !== 'READY') {
        return null
      }

      return mapRelativePosition(
        mouseCoordinates.clientX,
        mouseCoordinates.clientY
      )
    },
    [mapRelativePosition, status]
  )

  const shouldRenderAnnotation = useCallback(
    (annotation: AnnotationDotFragment) => {
      if (frameUUID) {
        // Frame view: only show annotations placed on this specific frame
        return (
          annotation.currentSubject.__typename === 'Artboard' &&
          annotation.currentSubject.uuid === frameUUID
        )
      }

      // Page view: show annotations on any artboard belonging to this page,
      // or annotations placed directly on the page itself.
      if (annotation.currentSubject.__typename === 'Artboard') {
        return annotation.currentSubject.page?.uuid === permanentPageId
      }
      if (annotation.currentSubject.__typename === 'Page') {
        return annotation.currentSubject.pageUUID === permanentPageId
      }

      return false
    },
    [permanentPageId, frameUUID]
  )

  const [hideAnnotationsDots] = useHideAnnotationDots()

  return (
    <DraftCommentProvider>
      <AnnotationOverlayContext
        onMovingAnnotation={setLockCameraState}
        getPositionRelativeToOrigin={getPositionRelativeToOrigin}
        getElementAtPosition={getElementAtPosition}
        hidden={hideAnnotationsDots || hidden}
        shouldRenderAnnotation={shouldRenderAnnotation}
        {...otherProps}
      />
    </DraftCommentProvider>
  )
}
